<template>
  <div class="ds-page">
    <!-- Page Header -->
    <div class="page-header d-print-none">
      <div class="row align-items-center">
        <div class="col">
          <div class="page-pretitle">{{ isCompanyView ? "Company" : "Companies" }}</div>
          <h2 class="page-title">AWA Request List</h2>
          <p class="page-subtitle">Monitor Advanced Wage Access requests, installment schedules, and disbursement</p>
        </div>
        <div class="col-auto ms-auto">
          <div class="awa-header-actions">
            <button class="ds-btn ds-btn--secondary" @click="getList()">
              <i class="el-icon-refresh"></i>
              Refresh
            </button>
            <button class="ds-btn ds-btn--blue" @click="downloadHandler">
              <font-awesome-icon icon="download" />
              Download
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Filter Card -->
    <div class="ds-card">
      <div class="ds-card__body">
        <div class="awa-filter-header">
          <span class="ds-filters__label">Filters</span>
          <span class="ds-filters__hint">Filter by date, status, sort order, and search by requestor</span>
        </div>

        <div class="awa-filter-two-col">
          <!-- Left -->
          <div class="awa-filter-left">
            <div class="awa-filter-grid-left">
              <div class="ds-field awa-field-full" v-if="!isStatusView">
                <label class="ds-field__label">Request Date</label>
                <el-date-picker
                  v-model="search.date"
                  type="daterange"
                  size="small"
                  :picker-options="datePickerOptions"
                  class="w-100"
                  range-separator="→"
                  value-format="yyyy-MM-dd"
                  start-placeholder="Start date"
                  end-placeholder="End date"
                />
              </div>

              <div class="ds-field" v-if="!isStatusView">
                <label class="ds-field__label">Filter By</label>
                <el-select size="small" v-model="search.filter_by" class="w-100">
                  <el-option v-for="(opt, i) in filter_by_options" :key="i" :value="opt.value" :label="opt.text" />
                </el-select>
              </div>

              <div class="ds-field" v-if="!isStatusView && search.filter_by === 'status'">
                <label class="ds-field__label">HR Approval Status</label>
                <el-select size="small" v-model="status" class="w-100">
                  <el-option label="All" value="" />
                  <el-option label="Waiting Approval" value="WAITING_APPROVAL" />
                  <el-option label="Approved" value="APPROVED" />
                  <el-option label="Rejected" value="REJECTED" />
                </el-select>
              </div>

              <div class="ds-field" v-if="!isStatusView && search.filter_by === 'disburse_status'">
                <label class="ds-field__label">Disbursement Status</label>
                <el-select size="small" v-model="disbursement_status" class="w-100">
                  <el-option label="All" value="" />
                  <el-option label="Waiting Approval" value="WAITING_APPROVAL" />
                  <el-option label="Waiting Disbursement" value="WAITING_DISBURSEMENT" />
                  <el-option label="Completed" value="COMPLETED" />
                  <el-option label="Failed" value="FAILED" />
                </el-select>
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Sort By</label>
                <el-select size="small" v-model="search.sort_by" class="w-100">
                  <el-option v-for="(opt, i) in sort_by_options" :key="i" :value="opt.value" :label="opt.text" />
                </el-select>
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Order</label>
                <el-select size="small" v-model="search.sort_type" class="w-100">
                  <el-option label="Descending" value="desc" />
                  <el-option label="Ascending" value="asc" />
                </el-select>
              </div>
            </div>
          </div>

          <!-- Separator -->
          <div class="awa-filter-sep"></div>

          <!-- Right: Search -->
          <div class="awa-filter-right">
            <div class="ds-field">
              <label class="ds-field__label">Search By</label>
              <el-select size="small" v-model="search.search" class="w-100">
                <el-option v-for="(opt, i) in search_options" :key="i" :value="opt.value" :label="opt.text" />
              </el-select>
            </div>

            <div class="ds-field">
              <label class="ds-field__label">Search Keyword</label>
              <el-input
                clearable
                size="small"
                type="text"
                v-model="search.search_value"
                placeholder="Input search keyword..."
                @clear="searchHandler"
              />
            </div>

            <div class="awa-filter-actions">
              <button @click="resetFilter" class="ds-btn ds-btn--secondary ds-btn--sm">Reset</button>
              <button @click="searchHandler" class="ds-btn ds-btn--blue ds-btn--sm">
                <font-awesome-icon icon="search" />
                Search
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Table Card -->
    <div class="ds-card">
      <div class="ds-table-wrap" v-loading="is_loading">
        <table class="ds-table">
          <thead>
            <tr>
              <th>Request Date</th>
              <th v-if="!isCompanyView">Company</th>
              <th>Requestor</th>
              <th>Bank</th>
              <th class="text-right">Amount</th>
              <th class="text-right">Admin Fee</th>
              <th class="text-right">Insurance Fee</th>
              <th class="text-right">Processing Fee</th>
              <th class="text-center">Tenor</th>
              <th>HR Approval</th>
              <th>Approval Date</th>
              <th>Disbursement</th>
              <th>Disburse Date</th>
              <th class="text-center">Detail</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in list" :key="i">
              <td>
                <span class="ds-text-sm ds-text-muted">{{ item.created_at_str }}</span>
              </td>
              <td v-if="!isCompanyView">
                <span class="ds-text-bold">{{ item.company?.company_name || "—" }}</span>
              </td>
              <td>
                <div class="ds-user-cell">
                  <div class="ds-avatar" :class="dsAvatarVariant(item.requestor?.full_name)">
                    {{ dsGetInitials(item.requestor?.full_name) }}
                  </div>
                  <div>
                    <div class="ds-user-cell__name">{{ item.requestor?.full_name || "—" }}</div>
                    <div class="ds-user-cell__sub">{{ item.requestor?.phone_number || "" }}</div>
                  </div>
                </div>
              </td>
              <td>
                <div class="ds-user-cell__name">{{ item.bank_account_number || "—" }}</div>
                <div class="ds-user-cell__sub">{{ item.bank_id || "" }}</div>
              </td>
              <td class="text-right">
                <span class="ds-amount">{{ item.request_amount_str }}</span>
              </td>
              <td class="text-right">
                <span class="ds-amount--muted">{{ item.admin_fee_str }}</span>
              </td>
              <td class="text-right">
                <span class="ds-amount--muted">{{ item.insurance_fee_str }}</span>
              </td>
              <td class="text-right">
                <span class="ds-amount--muted">{{ item.processing_fee_str }}</span>
              </td>
              <td class="text-center">
                <span class="ds-badge">{{ item.installment_periods || "—" }}</span>
              </td>
              <td>
                <span class="ds-pill" :class="dsStatusClass(item.status)">
                  {{ dsStatusLabel(item.status) }}
                </span>
              </td>
              <td>
                <span class="ds-text-sm ds-text-muted">
                  {{ item.status === "rejected" ? item.rejected_at_str : item.approved_at_str }}
                </span>
              </td>
              <td>
                <el-tooltip
                  v-if="item.disburse_status === 'failed' && item.failed_reason"
                  :content="item.failed_reason"
                  placement="left"
                >
                  <span class="ds-pill" :class="dsStatusClass(item.disburse_status)">
                    {{ dsStatusLabel(item.disburse_status) }}
                    <i class="el-icon-warning-outline" style="margin-left: 3px"></i>
                  </span>
                </el-tooltip>
                <span v-else class="ds-pill" :class="dsStatusClass(item.disburse_status)">
                  {{ dsStatusLabel(item.disburse_status) }}
                </span>
              </td>
              <td>
                <span class="ds-text-sm ds-text-muted">{{ item.disburse_at_str || "—" }}</span>
              </td>
              <td class="text-center">
                <el-tooltip content="View installment detail" placement="top">
                  <button @click="viewDetailTransaction(item)" class="awa-detail-btn">
                    <font-awesome-icon icon="eye" />
                  </button>
                </el-tooltip>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <el-empty v-if="!total_rows && !is_loading" description="No AWA requests found" :image="emptyStateImage" class="ds-empty" />

      <pagination :page="page" :per_page="per_page" :list="list" :total_rows="total_rows" @pageChange="pageChangeHandler" />
    </div>

    <!-- Detail Modal -->
    <b-modal size="xl" v-model="showDetailTransaction" :title="'AWA #' + selected_transaction.id" hide-footer>
      <div v-if="loading_detail" class="awa-modal-loading"><i class="el-icon-loading"></i> Loading...</div>

      <template v-else>
        <!-- Summary -->
        <el-descriptions class="mb-3" title="AWA Information" :column="2" border>
          <el-descriptions-item>
            <template slot="label"> <i class="el-icon-date"></i> Transaction Date </template>
            {{ formatDate(selected_transaction.created_at) }}
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label"> <i class="el-icon-s-finance"></i> Disbursement Status </template>
            <span class="ds-pill" :class="dsStatusClass(selected_transaction.disburse_status)">
              {{ dsStatusLabel(selected_transaction.disburse_status) }}
            </span>
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label"> <i class="el-icon-user"></i> User Name </template>
            {{ selected_transaction.requestor?.full_name || "—" }}
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label"> <i class="el-icon-document"></i> Disbursement Date </template>
            {{ formatDate(selected_transaction.disburse_at) }}
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label"> <i class="el-icon-message"></i> Email </template>
            {{ selected_transaction.requestor?.email || "—" }}
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label"> <i class="el-icon-office-building"></i> Company </template>
            {{ selected_transaction.company_name || "—" }}
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label"> <i class="el-icon-phone"></i> Phone Number </template>
            {{ selected_transaction.requestor?.phone_number || "—" }}
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label"> <i class="el-icon-money"></i> Processing Fee </template>
            {{ formatRupiah(selected_transaction.processing_fee) }}
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label"> <i class="el-icon-money"></i> Insurance Fee </template>
            {{ formatRupiah(selected_transaction.insurance_fee) }}
          </el-descriptions-item>
        </el-descriptions>

        <!-- Installments Table -->
        <div class="ds-table-wrap">
          <table class="ds-table">
            <thead>
              <tr>
                <th>Request ID</th>
                <th class="text-center">No.</th>
                <th>Due Date</th>
                <th class="text-right">Amount</th>
                <th class="text-right">Interest</th>
                <th class="text-right">Late Fee</th>
                <th class="text-right">Total</th>
                <th class="text-right">Paid</th>
                <th>Status</th>
                <th>Paid At</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="inst in awa_list_detail.flatMap((awa) => awa.installments)" :key="inst.id">
                <td>
                  <span class="ds-text-sm ds-text-muted">{{ inst.awa_request_id }}</span>
                </td>
                <td class="text-center">
                  <span class="ds-badge">{{ inst.installment_number }}</span>
                </td>
                <td>
                  <span class="ds-text-sm">{{ formatDate(inst.due_date) }}</span>
                </td>
                <td class="text-right">
                  <span class="ds-amount">{{ formatRupiah(inst.amount) }}</span>
                </td>
                <td class="text-right">
                  <span class="ds-amount--muted">{{ formatRupiah(inst.interest_amount) }}</span>
                </td>
                <td class="text-right">
                  <span class="ds-amount--muted">{{ formatRupiah(inst.late_fee) }}</span>
                </td>
                <td class="text-right">
                  <span class="ds-amount">{{ formatRupiah(inst.total_amount) }}</span>
                </td>
                <td class="text-right">
                  <span class="ds-amount--muted">{{ formatRupiah(inst.paid_amount) }}</span>
                </td>
                <td>
                  <span class="ds-pill" :class="inst.status_color">{{ inst.status_str }}</span>
                </td>
                <td>
                  <span class="ds-text-sm ds-text-muted">{{ formatDate(inst.paid_at) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment";
import { GET_DETAIL_AWALIST_FINANCE, GET_ALL_DETAIL_AWALIST, GET_DETAIL_AWA_INSTALLMENT } from "@/store/modules/awa";
import { designSystemMixin } from "@/mixins/designSystemMixin";
import emptyStateImage from "@/assets/images/undraw/empty-approval-history.svg";
import Pagination from "../../components/Pagination.vue";

const INSTALLMENT_STATUS = {
  WAITING_PAYMENT: { label: "Waiting Payment", color: "ds-pill--warning" },
  WAITING_APPROVAL: { label: "Waiting Approval", color: "ds-pill--info" },
  PARTIAL_PAID: { label: "Partial Paid", color: "ds-pill--info" },
  FULLY_PAID: { label: "Paid", color: "ds-pill--success" },
  CANCELLED: { label: "Cancelled", color: "ds-pill--danger" },
  REJECTED: { label: "Rejected", color: "ds-pill--danger" },
};

export default {
  name: "DetailAwaCompany",
  mixins: [designSystemMixin],
  components: { Pagination },
  metaInfo: {
    title: "Advanced Wage Access",
  },
  data() {
    return {
      emptyStateImage,
      is_loading: false,
      total_rows: 0,
      page: 1,
      per_page: 20,
      list: [],
      // detail modal
      showDetailTransaction: false,
      loading_detail: false,
      selected_transaction: {},
      awa_list_detail: [],
      // filters
      status: "",
      disbursement_status: "",
      search: {
        filter_by: "status",
        search_value: "",
        search: "full_name",
        date: [moment().subtract(30, "day").format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")],
        action: "",
        sort_by: "created_at",
        sort_type: "desc",
      },
      search_options: [
        { value: "full_name", text: "Requestor Name" },
        { value: "email", text: "Requestor Email" },
        { value: "phone_number", text: "Requestor Phone Number" },
        { value: "company_name", text: "Company Name" },
      ],
      filter_by_options: [
        { value: "status", text: "HR Approval Status" },
        { value: "disburse_status", text: "Disbursement Status" },
      ],
      sort_by_options: [
        { value: "created_at", text: "Created At" },
        { value: "updated_at", text: "Updated At" },
        { value: "disburse_at", text: "Disburse At" },
      ],
      datePickerOptions: {
        disabledDate: (date) => moment(date).isAfter(moment(), "days"),
      },
    };
  },
  computed: {
    isCompanyView() {
      return Boolean(this.$route.params.id);
    },
    isStatusView() {
      return Boolean(this.$route.params.status);
    },
  },
  mounted() {
    this.getList();
  },
  methods: {
    formatRupiah(value) {
      return this.dsFormatCurrency(value);
    },
    formatDate(date) {
      return this.dsFormatDateTime(date);
    },
    buildQuery() {
      let filterValue;
      if (this.search.filter_by === "disburse_status") {
        filterValue = this.isStatusView ? this.$route.params.status : this.disbursement_status;
      } else {
        filterValue = this.isStatusView ? this.$route.query.approval_status : this.status;
      }

      const q = {
        filter_value: filterValue,
        filter_by: this.search.filter_by,
        page: this.page,
        per_page: this.per_page,
        sort_by: this.search.sort_by,
        sort_type: this.search.sort_type,
        action: this.search.action,
        search: this.search.search,
        search_value: this.search.search_value,
      };

      if (!this.isStatusView && this.search.date) {
        q.start_date = this.search.date[0];
        q.end_date = this.search.date[1];
      }

      return q;
    },
    async getList() {
      this.is_loading = true;
      this.$parent.showLoader();
      try {
        const action = this.isCompanyView ? GET_DETAIL_AWALIST_FINANCE : GET_ALL_DETAIL_AWALIST;
        const opt = {
          query: this.buildQuery(),
          ...(this.isCompanyView ? { id: this.$route.params.id } : {}),
        };

        await this.$store.dispatch(action, opt).then((data) => {
          if (this.search.action === "download") {
            const anchor = document.createElement("a");
            anchor.href = `data:text/csv;charset=utf-8,${encodeURIComponent(data)}`;
            anchor.target = "_blank";
            anchor.download = "Advanced_Wage_Report.csv";
            anchor.click();
          }
        });

        if (this.search.action !== "download") {
          const { rows = [], count = 0 } = this.$store.getters.detail_awa_list || {};
          this.list = rows;
          this.total_rows = count;
        }
      } finally {
        this.$parent.hideLoader();
        this.is_loading = false;
      }
    },
    async viewDetailTransaction(item) {
      this.selected_transaction = item;
      this.showDetailTransaction = true;
      this.loading_detail = true;
      try {
        const { data } = await this.$store.dispatch(GET_DETAIL_AWA_INSTALLMENT, {
          company_id: item.company_id,
          id: item.id,
        });
        const rows = data?.data?.rows ?? [];
        this.awa_list_detail = rows.map((v) => ({
          ...v,
          installments: (v.installments || []).map((inst) => {
            const s = INSTALLMENT_STATUS[inst.status] || { label: inst.status || "—", color: "ds-pill--gray" };
            return { ...inst, status_str: s.label, status_color: s.color };
          }),
        }));
      } catch (e) {
        console.error("Failed to load AWA installment detail:", e);
        this.awa_list_detail = [];
      } finally {
        this.loading_detail = false;
      }
    },
    pageChangeHandler(page) {
      this.page = page;
      this.getList();
    },
    searchHandler() {
      this.search.action = "";
      this.page = 1;
      this.getList();
    },
    downloadHandler() {
      this.search.action = "download";
      this.page = 1;
      this.getList();
    },
    resetFilter() {
      this.status = "";
      this.disbursement_status = "";
      this.search.filter_by = "status";
      this.search.search_value = "";
      this.search.search = "full_name";
      this.search.sort_by = "created_at";
      this.search.sort_type = "desc";
      this.search.date = [moment().subtract(30, "day").format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")];
      this.page = 1;
      this.getList();
    },
  },
};
</script>

<style scoped>
/* ── Page Header Actions ─────────────────────────────────── */
.awa-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ── Filter Card ─────────────────────────────────────────── */
.awa-filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.awa-filter-two-col {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 0 20px;
  align-items: start;
}
.awa-filter-grid-left {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 12px;
  align-items: end;
}
.awa-field-full {
  grid-column: span 2;
}
.awa-filter-sep {
  width: 1px;
  background: rgba(101, 109, 119, 0.12);
  align-self: stretch;
  margin-top: 2px;
}
.awa-filter-right {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.awa-filter-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 4px;
}

/* ── Detail button ───────────────────────────────────────── */
.awa-detail-btn {
  background: #f4f6fa;
  border: 1px solid rgba(101, 109, 119, 0.24);
  border-radius: 6px;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #656d77;
  cursor: pointer;
  font-size: 12px;
  transition: background 0.15s;
}
.awa-detail-btn:hover {
  background: rgba(101, 109, 119, 0.12);
}

/* ── Modal loading ───────────────────────────────────────── */
.awa-modal-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #656d77;
  gap: 8px;
  font-size: 14px;
}

/* ── Table helpers ───────────────────────────────────────── */
.ds-text-sm {
  font-size: 12px;
}
.ds-text-muted {
  color: #8c98a4;
}
</style>
